import Cookie from "@/helpers/Cookie";
import { ELocale } from "@/providers/LocaleProvider/interfaces";

export const localeCookieName = "__locale";

/**
 * Emitted whenever the journey locale changes, so that consumers mounted above
 * LocaleProvider in the tree (CampaignProvider, see AppRouter) can react to it.
 * The new locale travels in the event detail: automatic detection changes the
 * locale without writing the cookie, so it cannot be read back from there.
 */
export const localeChangeEventName = "skinanalysia:localechange";

export type TLocaleChangeEvent = CustomEvent<{ locale: ELocale }>;

export const dispatchLocaleChange = (locale: ELocale): void => {
  window.dispatchEvent(
    new CustomEvent(localeChangeEventName, { detail: { locale } }),
  );
};

/**
 * Returns the default locale from the browser/device language only.
 * Used for initial default when no cookie is set.
 * Rules: fr → fr-FR, en → en-GB, it → it-IT, other → fr-FR.
 */
export const getDefaultLocaleFromNavigator = (): ELocale => {
  const languages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language || "fr"];
  for (const lang of languages) {
    const code = (lang || "").substring(0, 2).toLowerCase();
    if (code === "fr") return ELocale.frFr;
    if (code === "en") return ELocale.enGb;
    if (code === "it") return ELocale.itIt;
  }
  return ELocale.frFr;
};

/**
 * Current journey locale, resolved outside of React so that it stays readable
 * from providers mounted above LocaleProvider.
 */
export const getCurrentLocale = (): ELocale =>
  ((Cookie.get(localeCookieName) as ELocale) || null) ??
  getDefaultLocaleFromNavigator();
